/** @jsx jsx */

/** @jsxFrag React.Fragment */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';
import { ButtonBase } from '@coursera/cds-core';

import getPillCss, { classes } from 'bundles/browse-common/components/Pill/getPillCss';
import {
  useItemIndexInCollection,
  useItemsCollection,
  useRegisterItemInCollection,
} from 'bundles/browse-common/components/Pill/items-collection';

export type Props = BaseComponentProps<'button'> &
  Pick<HTMLAnchorElement, 'href'> & {
    /**
     * Pill label to display.
     */
    children: React.ReactNode;

    /**
     * Invoked when pill is clicked.
     */
    onClick?: () => void;
  };

const mergeRefs = <T = HTMLElement,>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> => {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null && typeof ref !== 'string') {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
};

/**
 * Pills are compact, interactive components that enable users navigate content experience.
 */
const Pill = React.forwardRef<HTMLButtonElement, Props>(function Pill(props, ref) {
  const { children, id: idFromProps, ...buttonProps } = props;

  const {
    id,
    ref: groupItemRef,
    commonProps,
  } = useRegisterItemInCollection<HTMLButtonElement, Pick<Props, 'role'>, { id?: string }>(
    {
      id: idFromProps,
    },
    [idFromProps]
  );

  const role = commonProps?.role || props.role;

  const collection = useItemsCollection<HTMLButtonElement>();
  const indexInPillsGroup = useItemIndexInCollection(id);

  let tabIndex: number | undefined;
  if (collection && role === 'radio') {
    const groupHasSelectedPills = collection.items.some((item) => collection.props[item.id].selected);

    if (groupHasSelectedPills) {
      tabIndex = -1;
    } else {
      tabIndex = indexInPillsGroup === 0 ? 0 : -1;
    }
  }

  return (
    <ButtonBase
      ref={mergeRefs([ref, groupItemRef])}
      classes={{
        root: clsx(classes.root),
      }}
      css={getPillCss}
      tabIndex={tabIndex}
      {...buttonProps}
      id={idFromProps}
      role={role}
      onClick={() => {
        props.onClick?.();
      }}
    >
      {children}
    </ButtonBase>
  );
});

export default Pill;
